import { differenceInMinutes, differenceInSeconds, format, startOfHour } from 'date-fns'
import {
  getDailyBuckets,
  getDateRange,
  getLast24HoursBuckets,
  isWithinRange,
  parseDate
} from './dateHelpers'

const getChatStatus = (chat) => {
  if (chat?.status) return chat.status
  if (chat?.closedAt || chat?.closed_at) return 'closed'
  return 'open'
}

const getChannelType = (chat) => {
  return chat?.channels?.[0]?.type ?? chat?.channel?.type ?? chat?.channel ?? 'Indefinido'
}

const getHandlingTimeMinutes = (chat) => {
  const attendedAt = parseDate(chat?.attendedAt ?? chat?.attended_at)
  const closedAt = parseDate(chat?.closedAt ?? chat?.closed_at)
  if (!attendedAt || !closedAt) return null
  return differenceInMinutes(closedAt, attendedAt)
}

const getResponseTimeSeconds = (chat) => {
  const attendedAt = parseDate(chat?.attendedAt ?? chat?.attended_at)
  const createdAt = parseDate(chat?.createdAt ?? chat?.created_at)
  if (!attendedAt || !createdAt) return null
  return differenceInSeconds(attendedAt, createdAt)
}

export const calculateMetrics = ({ chats = [], contacts = [], filters }) => {
  const totalChats = chats.length
  const openChats = chats.filter((chat) => getChatStatus(chat) === 'open').length
  const closedChats = chats.filter((chat) => getChatStatus(chat) === 'closed').length

  const handlingTimes = chats.map(getHandlingTimeMinutes).filter(Number.isFinite)
  const avgHandlingMinutes = handlingTimes.length > 0
    ? handlingTimes.reduce((sum, v) => sum + v, 0) / handlingTimes.length
    : null

  const responseChats = chats.filter((chat) => !!(chat?.attendedAt ?? chat?.attended_at))
  const responseRate = totalChats > 0 ? (responseChats.length / totalChats) * 100 : 0

  const channelCounts = chats.reduce((acc, chat) => {
    const channel = getChannelType(chat)
    acc[channel] = (acc[channel] ?? 0) + 1
    return acc
  }, {})

  const last24Buckets = getLast24HoursBuckets()
  const hourCounts = last24Buckets.reduce((acc, bucket) => {
    acc[bucket.label] = 0
    return acc
  }, {})

  chats.forEach((chat) => {
    const createdAt = parseDate(chat?.createdAt ?? chat?.created_at)
    if (!createdAt) return
    const hourKey = startOfHour(createdAt).getTime()
    const bucket = last24Buckets.find((entry) => entry.hour.getTime() === hourKey)
    if (bucket) hourCounts[bucket.label] += 1
  })

  const range = getDateRange(filters)
  const dailyBuckets = getDailyBuckets(range)

  const responseMap = new Map(
    dailyBuckets.map((bucket) => [format(bucket.day, 'yyyy-MM-dd'), { values: [] }])
  )

  chats.forEach((chat) => {
    const createdAt = parseDate(chat?.createdAt ?? chat?.created_at)
    if (!createdAt || !isWithinRange(createdAt, range)) return
    const responseSeconds = getResponseTimeSeconds(chat)
    if (!Number.isFinite(responseSeconds)) return
    const key = format(createdAt, 'yyyy-MM-dd')
    const entry = responseMap.get(key)
    if (entry) entry.values.push(responseSeconds)
  })

  const responseLabels = dailyBuckets.map((bucket) => bucket.label)
  const responseSeries = dailyBuckets.map((bucket) => {
    const entry = responseMap.get(format(bucket.day, 'yyyy-MM-dd'))
    if (!entry || entry.values.length === 0) return 0
    return entry.values.reduce((sum, v) => sum + v, 0) / entry.values.length
  })

  const dailyStatusMap = new Map(
    dailyBuckets.map((bucket) => [format(bucket.day, 'yyyy-MM-dd'), { open: 0, closed: 0 }])
  )

  chats.forEach((chat) => {
    const createdAt = parseDate(chat?.createdAt ?? chat?.created_at)
    if (!createdAt || !isWithinRange(createdAt, range)) return
    const key = format(createdAt, 'yyyy-MM-dd')
    const entry = dailyStatusMap.get(key)
    if (!entry) return
    if (getChatStatus(chat) === 'closed') {
      entry.closed += 1
    } else {
      entry.open += 1
    }
  })

  const dailyStatusLabels = dailyBuckets.map((bucket) => bucket.label)
  const dailyOpenSeries = dailyBuckets.map((bucket) => {
    const entry = dailyStatusMap.get(format(bucket.day, 'yyyy-MM-dd'))
    return entry?.open ?? 0
  })
  const dailyClosedSeries = dailyBuckets.map((bucket) => {
    const entry = dailyStatusMap.get(format(bucket.day, 'yyyy-MM-dd'))
    return entry?.closed ?? 0
  })

  return {
    totalChats,
    openChats,
    closedChats,
    avgHandlingMinutes,
    totalContacts: contacts.length,
    responseRate,
    channelCounts,
    hourCounts,
    responseSeries,
    responseLabels,
    dailyStatusLabels,
    dailyOpenSeries,
    dailyClosedSeries
  }
}
